import React from 'react';

/**
 * Parse text and convert URLs to clickable links
 *
 * Supports:
 * - http:// and https:// URLs
 * - www. URLs (adds https:// prefix)
 * - Plain domain URLs like example.com (adds https:// prefix)
 * - URLs with paths and query parameters
 * - Multiple URLs in the same message
 * - Intelligently handles trailing punctuation (.,!?;:)
 *
 * @param text - The text to parse
 * @returns React nodes with URLs converted to clickable links
 */
export function parseMessageWithLinks(text: string): React.ReactNode {
  // Regex to match URLs (http://, https://, www., or common TLDs)
  const urlRegex = /(https?:\/\/[^\s]+|www\.[^\s]+|[a-zA-Z0-9][a-zA-Z0-9-]*\.[a-zA-Z]{2,}[^\s]*)/g;

  // Split text by URLs, keeping the URLs in the result
  const parts = text.split(urlRegex);

  return parts.map((part, index) => {
    // Check if this part matches a URL pattern
    if (urlRegex.test(part)) {
      // Reset regex lastIndex after test()
      urlRegex.lastIndex = 0;

      // Remove trailing punctuation (including parentheses)
      const url = part.replace(/[.,!?;:)]+$/, '');
      const punctuation = part.slice(url.length);

      // Add https:// if needed
      const href = url.startsWith('http://') || url.startsWith('https://')
        ? url
        : `https://${url}`;

      return (
        <span key={index}>
          <a
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            className="underline hover:no-underline"
          >
            {url}
          </a>
          {punctuation}
        </span>
      );
    }

    // Regular text
    return <span key={index}>{part}</span>;
  });
}
